// Visitors can keep an old index.html cached by the CDN/browser for hours, and
// that HTML boots the previous build. This guard compares the running entry
// chunk with the published one and, on the next in-app navigation, hard-loads
// the page with a cache-busting parameter so the new build takes over.

const ENTRY_PATTERN = /\/assets\/(index-[\w-]+\.js)/u;
const CHECK_INTERVAL_MS = 60_000;
const BUILD_PARAM = "_build";
const RELOADED_KEY = "alfabetizando.build-reload";

export function entryFromHtml(html: string): string | null {
  return ENTRY_PATTERN.exec(html)?.[1] ?? null;
}

export function urlWithoutBuildParam(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has(BUILD_PARAM)) return null;
  url.searchParams.delete(BUILD_PARAM);
  return url.toString();
}

interface StaleBuildGuardOptions {
  readonly currentEntry: string | null;
  readonly fetchLatestHtml: () => Promise<string>;
  readonly now: () => number;
  readonly hardNavigate: (latestEntry: string) => void;
  readonly alreadyReloadedFor: (latestEntry: string) => boolean;
}

export function createStaleBuildGuard({
  alreadyReloadedFor,
  currentEntry,
  fetchLatestHtml,
  hardNavigate,
  now,
}: StaleBuildGuardOptions) {
  let lastCheckAt = Number.NEGATIVE_INFINITY;
  let latestEntry: string | null = null;

  async function refresh(): Promise<void> {
    if (!currentEntry || now() - lastCheckAt < CHECK_INTERVAL_MS) return;
    lastCheckAt = now();
    try {
      const entry = entryFromHtml(await fetchLatestHtml());
      if (entry && entry !== currentEntry) latestEntry = entry;
    } catch {
      // Offline or blocked: keep the running build.
    }
  }

  function reloadIfStale(): void {
    if (latestEntry && !alreadyReloadedFor(latestEntry)) hardNavigate(latestEntry);
  }

  return {
    async onNavigation(): Promise<void> {
      await refresh();
      reloadIfStale();
    },
    // Never reload while someone is looking at a page (they may be typing);
    // only remember the new build for the next navigation.
    async onVisible(): Promise<void> {
      await refresh();
    },
  };
}

interface NavigationSource {
  subscribe: (listener: (state: { location: { key: string } }) => void) => unknown;
}

export function installStaleBuildGuard(router: NavigationSource): void {
  const cleanUrl = urlWithoutBuildParam(window.location.href);
  if (cleanUrl) window.history.replaceState(window.history.state, "", cleanUrl);

  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  const guard = createStaleBuildGuard({
    currentEntry: script ? entryFromHtml(script.src) : null,
    fetchLatestHtml: async () => {
      const response = await fetch(`/index.html?${BUILD_PARAM}=${String(Date.now())}`, { cache: "no-store" });
      if (!response.ok) throw new Error("build_check_failed");
      return response.text();
    },
    now: () => Date.now(),
    alreadyReloadedFor: (entry) => {
      try { return window.sessionStorage.getItem(RELOADED_KEY) === entry; } catch { return false; }
    },
    hardNavigate: (entry) => {
      try { window.sessionStorage.setItem(RELOADED_KEY, entry); } catch { /* private mode */ }
      const url = new URL(window.location.href);
      url.searchParams.set(BUILD_PARAM, entry);
      window.location.replace(url.toString());
    },
  });

  let lastKey: string | null = null;
  router.subscribe(({ location }) => {
    if (location.key === lastKey) return;
    lastKey = location.key;
    void guard.onNavigation();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void guard.onVisible();
  });
}
